import { GQL, getQuota, isOverQuota } from "platform-common/index"
import * as React from "react"

export type QuotaStatus = {
  used: number | null
  limit: number | null
  isOverQuota: boolean
}

export const useBilling = () => {
  // send user to edit payment and subscription settings
  const [createPortalSession, { loading: createPortalSessionLoading }] = GQL.useCreateBillingPortalSessionMutation()
  const redirectToBillingSettings = React.useCallback(
    async (flowType: GQL.FlowType) => {
      const returnUrl = window.location.href
      const res = await createPortalSession({ variables: { returnUrl, flowType } })
      const redirectUrl = res.data?.createCustomerPortalSession.redirectUrl
      if (redirectUrl) {
        window.location.href = redirectUrl
      } else {
        console.error(res.errors)
      }
    },
    [createPortalSession]
  )

  // begin a new subscription
  // checkout a product/price
  const [initiateCheckoutSession, { loading: redirectToCheckoutLoading }] = GQL.useInitiateCheckoutSessionMutation()
  const redirectToCheckout = React.useCallback(
    async (priceId: string) => {
      const returnUrl = window.location.href
      const res = await initiateCheckoutSession({
        variables: { input: { cancelUrl: returnUrl, successUrl: returnUrl, priceId } },
      })
      const startUrl = res.data?.initiateCheckout.startUrl
      if (startUrl) {
        window.location.href = startUrl
      } else {
        console.error(res.errors)
      }
    },
    [initiateCheckoutSession]
  )

  // modify existing subscription
  // upgrade/downgrade subscription plan
  const [modifySubscription, { loading: modifySubscriptionLoading }] = GQL.useModifySubscriptionMutation()

  // fetch available products
  const [getBillingStatusQuery, { loading: billingStatusLoading, data: billingStatus }] =
    GQL.useGetBillingStatusLazyQuery({ fetchPolicy: "cache-and-network" })
  const getBillingStatus = React.useCallback(async () => {
    return await getBillingStatusQuery()
  }, [getBillingStatusQuery])

  // look up quota usage for a given feature
  const getQuotaStatus = React.useCallback(
    (feature: GQL.Feature): QuotaStatus => {
      const quotas = billingStatus?.getBillingStatus.quotas
      const minutesQuota = getQuota(quotas, feature)
      const isOverQuota_ = isOverQuota(quotas, feature)
      const { used, limit } = minutesQuota || {}
      return { used: used ?? null, limit: limit ?? null, isOverQuota: isOverQuota_ }
    },
    [billingStatus]
  )

  return {
    getQuotaStatus,
    redirectToBillingSettings,
    getBillingStatus,
    billingStatus,
    billingStatusLoading,
    createPortalSessionLoading,
    redirectToCheckout,
    redirectToCheckoutLoading,
    modifySubscription,
    modifySubscriptionLoading,
    isPaidUser: Boolean(billingStatus && billingStatus?.getBillingStatus.accountType !== "FREE"),
  }
}
